'use client';

import * as React from 'react';
import type { Table, Column } from '@tanstack/react-table';
import { Plus, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Filter operators for different data types
 */
export type FilterOperator =
  | 'contains'
  | 'equals'
  | 'startsWith'
  | 'endsWith'
  | 'eq'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'before'
  | 'after'
  | 'between';

/**
 * Column type for determining available operators
 */
export type ColumnType = 'text' | 'number' | 'date' | 'select';

/**
 * Filter rule definition
 */
export interface FilterRule {
  /** Unique identifier for the rule */
  id: string;
  /** Column ID to filter on */
  column: string;
  /** Filter operator */
  operator: FilterOperator;
  /** Filter value(s) */
  value: unknown;
}

/**
 * Saved filter preset
 */
export interface SavedFilter {
  /** Unique identifier */
  id: string;
  /** Filter name */
  name: string;
  /** Filter rules */
  rules: FilterRule[];
  /** Logic operator (AND/OR) */
  logic: 'AND' | 'OR';
}

/**
 * Props for the DataTableFilterBuilder component
 */
export interface DataTableFilterBuilderProps<TData> {
  /** TanStack Table instance */
  table: Table<TData>;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback when filters are applied */
  onApply: (filters: FilterRule[], logic: 'AND' | 'OR') => void;
  /** Optional saved filters */
  savedFilters?: SavedFilter[] | undefined;
  /** Callback when a filter is saved */
  onSaveFilter?: ((name: string, filters: FilterRule[], logic: 'AND' | 'OR') => void) | undefined;
  /** Callback when a saved filter is loaded */
  onLoadFilter?: ((filter: SavedFilter) => void) | undefined;
  /** Optional function to determine column type */
  getColumnType?: ((columnId: string) => ColumnType) | undefined;
}

/**
 * Get available operators for a column type
 */
function getOperatorsForType(type: ColumnType): { value: FilterOperator; label: string }[] {
  switch (type) {
    case 'text':
      return [
        { value: 'contains', label: 'Contains' },
        { value: 'equals', label: 'Equals' },
        { value: 'startsWith', label: 'Starts with' },
        { value: 'endsWith', label: 'Ends with' },
      ];
    case 'number':
      return [
        { value: 'eq', label: 'Equals' },
        { value: 'gt', label: 'Greater than' },
        { value: 'lt', label: 'Less than' },
        { value: 'gte', label: 'Greater than or equal' },
        { value: 'lte', label: 'Less than or equal' },
      ];
    case 'date':
      return [
        { value: 'before', label: 'Before' },
        { value: 'after', label: 'After' },
        { value: 'between', label: 'Between' },
      ];
    case 'select':
      return [{ value: 'equals', label: 'Equals' }];
    default:
      return [{ value: 'equals', label: 'Equals' }];
  }
}

/**
 * DataTableFilterBuilder Component
 *
 * An advanced filter builder dialog for creating complex filter rules on table data.
 * Supports multiple filter conditions, different operators based on column types,
 * AND/OR logic, and saving/loading filter presets.
 *
 * Features:
 * - Add/remove multiple filter rules
 * - Column selection with type-aware operators
 * - AND/OR logic toggle
 * - Save filter configurations
 * - Load previously saved filters
 * - Keyboard accessible
 * - Mobile responsive
 *
 * @example
 * ```tsx
 * <DataTableFilterBuilder
 *   table={table}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   onApply={(filters, logic) => applyFilters(filters, logic)}
 *   savedFilters={savedFilters}
 *   onSaveFilter={(name, filters, logic) => saveFilter(name, filters, logic)}
 *   getColumnType={(columnId) => getColumnType(columnId)}
 * />
 * ```
 */
export function DataTableFilterBuilder<TData>({
  table,
  open,
  onOpenChange,
  onApply,
  savedFilters = [],
  onSaveFilter,
  onLoadFilter,
  getColumnType,
}: DataTableFilterBuilderProps<TData>) {
  const [rules, setRules] = React.useState<FilterRule[]>([]);
  const [logic, setLogic] = React.useState<'AND' | 'OR'>('AND');
  const [filterName, setFilterName] = React.useState('');
  const [showSaveInput, setShowSaveInput] = React.useState(false);

  // Get all filterable columns
  const columns = table.getAllColumns().filter((column) => column.getCanFilter());

  // Initialize with one empty rule when dialog opens
  React.useEffect(() => {
    if (open && rules.length === 0 && columns.length > 0) {
      addRule();
    }
  }, [open, columns.length]);

  /**
   * Add a new filter rule
   */
  const addRule = () => {
    const firstColumn = columns[0];
    if (!firstColumn) return;

    const columnType = getColumnType ? getColumnType(firstColumn.id) : 'text';
    const operators = getOperatorsForType(columnType);

    const newRule: FilterRule = {
      id: crypto.randomUUID(),
      column: firstColumn.id,
      operator: operators[0]!.value,
      value: '',
    };

    setRules((prev) => [...prev, newRule]);
  };

  /**
   * Remove a filter rule
   */
  const removeRule = (ruleId: string) => {
    setRules((prev) => prev.filter((rule) => rule.id !== ruleId));
  };

  /**
   * Update a filter rule
   */
  const updateRule = (ruleId: string, updates: Partial<FilterRule>) => {
    setRules((prev) =>
      prev.map((rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule))
    );
  };

  /**
   * Handle column change for a rule
   */
  const handleColumnChange = (ruleId: string, columnId: string) => {
    const columnType = getColumnType ? getColumnType(columnId) : 'text';
    const operators = getOperatorsForType(columnType);

    updateRule(ruleId, {
      column: columnId,
      operator: operators[0]!.value,
      value: '',
    });
  };

  /**
   * Apply filters and close dialog
   */
  const handleApply = () => {
    // Filter out rules with empty values
    const validRules = rules.filter((rule) => rule.value !== '' && rule.value != null);
    onApply(validRules, logic);
    onOpenChange(false);
  };

  /**
   * Cancel and close dialog
   */
  const handleCancel = () => {
    onOpenChange(false);
  };

  /**
   * Save current filter configuration
   */
  const handleSaveFilter = () => {
    if (!filterName.trim() || !onSaveFilter) return;

    const validRules = rules.filter((rule) => rule.value !== '' && rule.value != null);
    onSaveFilter(filterName.trim(), validRules, logic);
    setFilterName('');
    setShowSaveInput(false);
  };

  /**
   * Load a saved filter
   */
  const handleLoadFilter = (savedFilterId: string) => {
    const savedFilter = savedFilters.find((f) => f.id === savedFilterId);
    if (!savedFilter) return;

    setRules(savedFilter.rules);
    setLogic(savedFilter.logic);

    if (onLoadFilter) {
      onLoadFilter(savedFilter);
    }
  };

  /**
   * Clear all rules
   */
  const handleClearAll = () => {
    setRules([]);
    setLogic('AND');
  };

  /**
   * Get column display name
   */
  const getColumnDisplayName = (column: Column<TData, unknown>) => {
    return typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : column.id;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Advanced Filters</DialogTitle>
          <DialogDescription>
            Create complex filter rules to narrow down your data. Combine multiple conditions
            using AND/OR logic.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Saved Filters */}
          {savedFilters.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-primary">Load Saved Filter</label>
              <Select onValueChange={handleLoadFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a saved filter..." />
                </SelectTrigger>
                <SelectContent>
                  {savedFilters.map((filter) => (
                    <SelectItem key={filter.id} value={filter.id}>
                      {filter.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Logic Selector */}
          {rules.length > 1 && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-text-primary">Match:</label>
              <div className="flex gap-2">
                <Button
                  variant={logic === 'AND' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setLogic('AND')}
                >
                  All (AND)
                </Button>
                <Button
                  variant={logic === 'OR' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setLogic('OR')}
                >
                  Any (OR)
                </Button>
              </div>
            </div>
          )}

          {/* Filter Rules */}
          <div className="space-y-3">
            {rules.length === 0 ? (
              <div className="rounded-lg border border-border-primary bg-neutral-50 p-6 text-center dark:bg-neutral-900">
                <p className="text-sm text-text-secondary">
                  No filter rules yet. Click "Add Rule" to get started.
                </p>
              </div>
            ) : (
              rules.map((rule, index) => {
                const columnType = getColumnType
                  ? getColumnType(rule.column)
                  : 'text';
                const operators = getOperatorsForType(columnType);

                return (
                  <div
                    key={rule.id}
                    className="flex flex-col gap-2 rounded-lg border border-border-primary bg-background-primary p-4 sm:flex-row sm:items-start"
                  >
                    {/* Rule Number */}
                    <div className="flex items-center gap-2 sm:items-start sm:pt-2">
                      <span className="text-sm font-medium text-text-secondary">
                        {index + 1}.
                      </span>
                    </div>

                    {/* Column Select */}
                    <div className="flex-1 space-y-1">
                      <label className="text-xs text-text-secondary">Column</label>
                      <Select
                        value={rule.column}
                        onValueChange={(value) => handleColumnChange(rule.id, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {columns.map((col) => (
                            <SelectItem key={col.id} value={col.id}>
                              {getColumnDisplayName(col)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Operator Select */}
                    <div className="flex-1 space-y-1">
                      <label className="text-xs text-text-secondary">Operator</label>
                      <Select
                        value={rule.operator}
                        onValueChange={(value) =>
                          updateRule(rule.id, { operator: value as FilterOperator })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {operators.map((op) => (
                            <SelectItem key={op.value} value={op.value}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Value Input */}
                    <div className="flex-1 space-y-1">
                      <label className="text-xs text-text-secondary">Value</label>
                      <Input
                        type={columnType === 'number' ? 'number' : 'text'}
                        value={rule.value as string | number | readonly string[] | undefined}
                        onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                        placeholder="Enter value..."
                      />
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRule(rule.id)}
                      className="self-start sm:mt-6"
                      aria-label="Remove rule"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          {/* Add Rule Button */}
          {columns.length > 0 && (
            <Button
              variant="outline"
              size="md"
              onClick={addRule}
              leftIcon={<Plus className="h-4 w-4" />}
              className="w-full"
            >
              Add Rule
            </Button>
          )}

          {/* Save Filter Section */}
          {onSaveFilter && rules.length > 0 && (
            <div className="space-y-2 border-t border-border-primary pt-4">
              {!showSaveInput ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSaveInput(true)}
                  leftIcon={<Save className="h-4 w-4" />}
                >
                  Save this filter
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder="Filter name..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveFilter();
                      }
                    }}
                  />
                  <Button size="sm" onClick={handleSaveFilter} disabled={!filterName.trim()}>
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowSaveInput(false);
                      setFilterName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* No Columns Message */}
          {columns.length === 0 && (
            <div className="rounded-lg border border-border-primary bg-neutral-50 p-6 text-center dark:bg-neutral-900">
              <p className="text-sm text-text-secondary">
                No filterable columns available in this table.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          {rules.length > 0 && (
            <Button variant="secondary" onClick={handleClearAll}>
              Clear All
            </Button>
          )}
          <Button onClick={handleApply} disabled={rules.length === 0}>
            Apply Filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
